import type { ChartOptions, TooltipItem } from "chart.js";
import { getSharedPlugins } from "./chartDefaults";
import {
  buildChapterAnnotations,
  type ChapterMilestone,
} from "./buildChapterAnnotations";
import { secondsToMinutes } from "../../../projects/helpers/time/secondsToMinutes";

/**
 * Maps position points to the {xValues, labels} a timestamp line chart needs:
 * xValues drive the linear seconds axis, labels are the mm:ss tooltip titles.
 */
export const getTimestampSeries = (
  points: { position_seconds: number }[]
): { xValues: number[]; labels: string[] } => ({
  xValues: points.map((p) => p.position_seconds),
  labels: points.map((p) => secondsToMinutes(p.position_seconds)),
});

interface TimestampChartOptionsArgs {
  /** Chapter milestones drawn as vertical lines. */
  chapters?: ChapterMilestone[];
  /** Extra scale overrides merged on top of the chart defaults (e.g. y axis). */
  scales?: ChartOptions<"line">["scales"];
  /** Optional tooltip body line; the title is always the mm:ss timestamp. */
  tooltipLabel?: (ctx: TooltipItem<"line">) => string;
}

/**
 * Shared chart options for the timestamp-based position charts: a mm:ss tooltip
 * title, chapter annotation lines, and any extra scales/tooltip-label overrides.
 */
export const buildTimestampChartOptions = ({
  chapters,
  scales,
  tooltipLabel,
}: TimestampChartOptionsArgs): Partial<ChartOptions<"line">> => {
  const shared = getSharedPlugins();
  return {
    ...(scales ? { scales } : {}),
    plugins: {
      ...shared,
      tooltip: {
        ...shared.tooltip,
        callbacks: {
          title: (items: TooltipItem<"line">[]) => {
            const first = items[0];
            return first ? secondsToMinutes(Number(first.parsed.x)) : "";
          },
          ...(tooltipLabel ? { label: tooltipLabel } : {}),
        },
      },
      annotation: { annotations: buildChapterAnnotations(chapters) },
    },
  } as Partial<ChartOptions<"line">>;
};
